import { createAppHeader, createLocaleSwitcher } from "./app-header";
import { localeOptionAutonym } from "../../lib/locale-autonym";
import type { Translate } from "../../lib/i18n";
import { writeStoredLocale } from "../../lib/locale-storage";
import type { SupportedLocale } from "../../locales/catalog-registry";

/** Matches **`ux/home-page`**: globe glyph ahead of the locale **`select`**. */
function createLocaleGlobeIcon(): SVGSVGElement {
  const ns = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(ns, "svg") as SVGSVGElement;
  svg.setAttribute("width", "18");
  svg.setAttribute("height", "18");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.classList.add("home-header__locale-globe-svg");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke-width", "1.5");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");

  const circle = document.createElementNS(ns, "circle");
  circle.setAttribute("cx", "12");
  circle.setAttribute("cy", "12");
  circle.setAttribute("r", "10");
  const meridian = document.createElementNS(ns, "ellipse");
  meridian.setAttribute("cx", "12");
  meridian.setAttribute("cy", "12");
  meridian.setAttribute("rx", "4");
  meridian.setAttribute("ry", "10");
  const equator = document.createElementNS(ns, "path");
  equator.setAttribute("d", "M2 12h20");
  svg.appendChild(circle);
  svg.appendChild(meridian);
  svg.appendChild(equator);
  return svg;
}

/**
 * Home (**`/`**) uses a labelled globe cluster; other routes use the bare **`select`**.
 *
 * @param localeSwitcher - Locale **`select`** produced alongside this cluster.
 */
function wrapGlobeLocaleCluster(localeSwitcher: HTMLElement): HTMLElement {
  const cluster = document.createElement("span");
  cluster.className = "home-header__locale-cluster";
  cluster.appendChild(createLocaleGlobeIcon());
  cluster.appendChild(localeSwitcher);
  return cluster;
}

/** How the locale control appears in the **`qz-app-header`** trail. */
export type SiteAppHeaderLocalePresentation = "plain" | "globe";

export interface StandardSiteAppHeaderResult {
  /** Full **`qz-app-header`** with **`home-header`** layout class. */
  header: HTMLElement;
  /**
   * Trailing slot for organizer auth (**`createOrganizerAccountMenu`** or home **`sign-in`** link).
   * Keeps **`home-header__auth-slot`** for existing **`CSS`** hooks.
   */
  authSlot: HTMLElement;
}

/**
 * Builds the shared top bar: home brand, locale control, and an empty organizer auth slot.
 *
 * Call **`attachOrganizerAccountMenuIfAuthenticated`** on **`authSlot`** when the view should
 * show the account menu after **`GET`** **`/api/v1/auth/me`**. The home route fills **`authSlot`**
 * from its own session probe instead.
 *
 * @param translate - Localized string resolver.
 * @param locale - Active **`SupportedLocale`** for the picker value.
 * @param options - **`localePresentation`** selects globe-plus-picker versus picker alone (default **`plain`**).
 */
export function createStandardSiteAppHeader(
  translate: Translate,
  locale: SupportedLocale,
  options?: Readonly<{ localePresentation?: SiteAppHeaderLocalePresentation }>,
): StandardSiteAppHeaderResult {
  const localeSwitcher = createLocaleSwitcher(
    locale,
    translate("quiz.ui_demo.locale_picker_label"),
    (loc) => localeOptionAutonym(loc),
    (next) => {
      writeStoredLocale(next);
      window.location.reload();
    },
  );

  const localeTrail: HTMLElement =
    options?.localePresentation === "globe"
      ? wrapGlobeLocaleCluster(localeSwitcher)
      : localeSwitcher;

  const authSlot = document.createElement("span");
  authSlot.className = "home-header__auth-slot qz-site-header__auth-slot";

  const header = createAppHeader({
    title: translate("quiz.home.header_title"),
    trailing: [localeTrail, authSlot],
    homeBrand: true,
  });
  header.classList.add("home-header");

  return { header, authSlot };
}
